import { useEffect, useMemo, useState } from 'react';
import type { FloatingNavConfig, FloatingNavState } from '../../domain/models/NewHomePageData';

export type FloatingNavStateName = 'expandedCenter' | 'collapsedLogo' | 'expandedRight';

export interface UseFloatingNavReturn {
  currentState: FloatingNavStateName;
  stateConfig: FloatingNavState;
  transition: FloatingNavConfig['transition'];
}

export function useFloatingNavigation(config: FloatingNavConfig): UseFloatingNavReturn {
  // Initialise from the real scroll position so a page-refresh with restored scroll
  // doesn't trigger the slide-in animation (avoids starting from off-screen top-left).
  const [scrollY, setScrollY] = useState(() =>
    typeof window !== 'undefined' ? window.scrollY : 0
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let rafId: number | null = null;

    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setScrollY(window.scrollY);
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  const currentState = useMemo((): FloatingNavStateName => {
    if (typeof document === 'undefined') return 'expandedCenter';

    const hasReachedSection = (sectionName: string): boolean => {
      if (!sectionName) return false;
      const section = document.querySelector(
        `[data-analytics-section-name="${sectionName}"]`,
      );
      return section !== null && section.getBoundingClientRect().top <= window.innerHeight;
    };

    if (hasReachedSection(config.expandAtSectionName)) return 'expandedRight';
    if (hasReachedSection(config.collapseAtSectionName)) return 'collapsedLogo';
    return 'expandedCenter';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY,config.collapseAtSectionName, config.expandAtSectionName]);

  return {
    currentState,
    stateConfig: config.states[currentState],
    transition: config.transition,
  };
}
